import React from "react";
import { motion } from "framer-motion";

const T = {
  bg: "#050d1a",
  bgCard: "#0a1628",
  border: "#1a2d4a",
  accent: "#eab308",
  accentDim: "rgba(234,179,8,0.12)",
  blue: "#3b82f6",
  textPrimary: "#f0f6ff",
  textSecondary: "#94a3b8",
  textMuted: "#4b6280",
  success: "#22c55e",
  danger: "#ef4444",
};

const CoinRainUI = ({ coins, points, timeLeft, screenSize, gameActive, onBTCTap }) => {
  const isMobile = screenSize === "mobile";
  const isTablet = screenSize === "tablet";
  const isDesktop = screenSize === "desktop";
  const gameWidth = isMobile ? 300 : isTablet ? 600 : 800;
  const progressPct = (points / 100) * 100;

  return (
    <>
      {/* ── Mobile/Tablet top HUD ── */}
      {!isDesktop && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, height: 72,
          background: "rgba(5,13,26,0.96)",
          backdropFilter: "blur(16px)",
          borderBottom: `1px solid ${T.border}`,
          zIndex: 50,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "0 16px",
        }}>
          <div style={{ width: "100%", maxWidth: 440 }}>
            {/* progress bar */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8,
            }}>
              <span style={{ fontSize: 12, fontWeight: 900, color: T.accent, letterSpacing: "0.1em" }}>
                {points} / 100 PTS
              </span>
              <span style={{
                fontSize: 14, fontWeight: 900,
                color: timeLeft < 10 ? T.danger : T.textPrimary,
              }}>
                {timeLeft}s
              </span>
            </div>
            <div style={{ height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 999, overflow: "hidden" }}>
              <motion.div
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.4 }}
                style={{
                  height: "100%", borderRadius: 999,
                  background: `linear-gradient(90deg,${T.accent},#f59e0b)`,
                  boxShadow: "0 0 10px rgba(234,179,8,0.5)",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Main layout ── */}
      <div style={{
        position: "fixed", inset: 0,
        paddingTop: isDesktop ? 0 : 72,
        zIndex: 40, overflow: "hidden",
        background: T.bg,
        display: isDesktop ? "flex" : "block",
      }}>

        {/* grid bg */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          backgroundImage: `
            linear-gradient(rgba(234,179,8,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59,130,246,0.025) 1px, transparent 1px)
          `,
          backgroundSize: "44px 44px",
        }} />

        {/* ── GAME AREA ── */}
        <div style={{
          position: "relative", overflow: "hidden",
          width: isDesktop ? "60%" : "100%",
          height: "100%",
          padding: isMobile ? "12px 12px 0" : "20px 24px 0",
        }}>
          {/* instruction badge */}
          <div style={{
            position: "absolute",
            top: isMobile ? 16 : 24,
            left: isMobile ? 12 : 24,
            zIndex: 30, pointerEvents: "none",
            display: "flex", alignItems: "center", gap: 8,
            background: T.bgCard,
            border: `1px solid rgba(234,179,8,0.3)`,
            padding: "7px 14px", borderRadius: 999,
            fontSize: isMobile ? 10 : 12, fontWeight: 900,
            color: T.accent, letterSpacing: "0.05em",
            boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          }}>
            <span>₿</span> Tap ONLY Gold BTC Coins
          </div>

          {/* falling coins */}
          <div style={{ width: "100%", height: "100%", position: "relative" }}>
            {coins.map(coin => {
              if (coin.x > gameWidth) return null;
              const isBTC = coin.type === "BTC";
              const baseSize = isMobile ? 34 : isTablet ? 38 : 44;
              const size = baseSize * coin.size;
              return (
                <motion.div
                  key={coin.id}
                  style={{
                    position: "absolute",
                    left: coin.x,
                    top: coin.y,
                    width: size, height: size,
                    borderRadius: "50%",
                    background: isBTC
                      ? `linear-gradient(135deg,#92400e,${T.accent})`
                      : "linear-gradient(135deg,#1e3a5f,#2a4a6e)",
                    border: isBTC
                      ? "2px solid rgba(234,179,8,0.6)"
                      : `1px solid ${T.border}`,
                    boxShadow: isBTC
                      ? "0 0 18px rgba(234,179,8,0.45), 0 4px 12px rgba(0,0,0,0.5)"
                      : "0 4px 12px rgba(0,0,0,0.4)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: isBTC ? "pointer" : "default",
                    userSelect: "none",
                    touchAction: "none",
                    zIndex: 20,
                  }}
                  initial={{ y: -120, opacity: 0 }}
                  animate={{ y: window.innerHeight, opacity: [1, 1, 0], rotate: [0, 360] }}
                  transition={{ duration: 7, ease: "linear" }}
                  onClick={() => isBTC && onBTCTap(coin.id)}
                  whileTap={isBTC ? { scale: 0.85 } : {}}
                >
                  <span style={{
                    fontSize: size * 0.38, fontWeight: 900,
                    color: isBTC ? "#0a0d14" : T.textMuted,
                    lineHeight: 1,
                  }}>
                    {isBTC ? "₿" : coin.type.slice(0, 1)}
                  </span>

                  {/* gold shimmer on BTC */}
                  {isBTC && (
                    <motion.div
                      animate={{ x: ["-120%", "120%"] }}
                      transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
                      style={{
                        position: "absolute", inset: 0, borderRadius: "50%",
                        background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.25),transparent)",
                        pointerEvents: "none",
                      }}
                    />
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* ── Desktop right panel ── */}
        {isDesktop && (
          <div style={{
            width: "40%", height: "100%",
            background: T.bgCard,
            borderLeft: `1px solid ${T.border}`,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            padding: 48, gap: 36,
            position: "relative", overflow: "hidden",
          }}>
            {/* top accent */}
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 3,
              background: `linear-gradient(90deg,${T.accent},${T.blue},transparent)`,
            }} />
            <div style={{
              position: "absolute", top: "-20%", right: "-20%",
              width: 280, height: 280, borderRadius: "50%",
              background: "radial-gradient(ellipse,rgba(234,179,8,0.08) 0%,transparent 70%)",
              pointerEvents: "none",
            }} />

            {/* BTC icon */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              style={{
                width: 96, height: 96, borderRadius: 28,
                background: `linear-gradient(135deg,#92400e,${T.accent})`,
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                boxShadow: "0 16px 40px rgba(234,179,8,0.35)",
              }}
            >
              <span style={{ fontSize: 38, fontWeight: 900, color: "#0a0d14" }}>₿</span>
              <span style={{ fontSize: 8, fontWeight: 900, color: "rgba(10,13,20,0.6)", letterSpacing: "0.15em", textTransform: "uppercase" }}>
                BTC Hunter
              </span>
            </motion.div>

            {/* score */}
            <div style={{ textAlign: "center", position: "relative", zIndex: 1 }}>
              <div style={{
                fontSize: 72, fontWeight: 900, color: T.textPrimary,
                letterSpacing: "-0.05em", lineHeight: 1,
              }}>
                {points}
                <span style={{ fontSize: 32, color: T.textMuted }}>/100</span>
              </div>

              <div style={{
                width: "100%", height: 12,
                background: "rgba(255,255,255,0.05)",
                borderRadius: 999, overflow: "hidden",
                marginTop: 20,
              }}>
                <motion.div
                  animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 0.5 }}
                  style={{
                    height: "100%", borderRadius: 999,
                    background: `linear-gradient(90deg,${T.accent},#f59e0b)`,
                    boxShadow: "0 0 12px rgba(234,179,8,0.5)",
                  }}
                />
              </div>

              <motion.div
                animate={timeLeft < 10 ? { scale: [1, 1.04, 1] } : {}}
                transition={{ duration: 0.6, repeat: Infinity }}
                style={{
                  fontSize: 64, fontWeight: 900, marginTop: 24,
                  color: timeLeft < 10 ? T.danger : T.textPrimary,
                  letterSpacing: "-0.04em",
                }}
              >
                {timeLeft}
                <span style={{ fontSize: 22, color: T.textMuted }}>s</span>
              </motion.div>
            </div>

            {/* tip badge */}
            <div style={{
              padding: "12px 20px", borderRadius: 14,
              background: T.accentDim,
              border: `1px solid rgba(234,179,8,0.25)`,
              fontSize: 11, fontWeight: 700, color: "#fde68a",
              textAlign: "center", lineHeight: 1.5,
            }}>
              Only <strong style={{ color: T.accent }}>Gold ₿</strong> coins give points.<br />
              Other coins are decoys!
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CoinRainUI;